import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { Toaster } from 'react-hot-toast';
import { store } from './store';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import TasksPage from './features/tasks/TasksPage';
import TaskDetailsPage from './features/tasks/TaskDetailsPage';
import ProjectIntakePage from './features/project-intake/ProjectIntakePage';
import FormBuilderPage from './features/forms/FormBuilderPage';
import FormSubmissionsPage from './features/forms/FormSubmissionsPage';
import FormsListPage from './features/forms/FormsListPage';
import PublicFormPage from './features/forms/PublicFormPage';
import VerifySharedFormPage from './features/forms/VerifySharedFormPage';
import DynamicFormPage from './features/forms/DynamicFormPage';
import FormFeedsPage from './features/form-feeds/FormFeedsPage';
import CreateFeedPage from './features/form-feeds/CreateFeedPage';
import EditFeedPage from './features/form-feeds/EditFeedPage';
import FieldMappingPage from './features/form-feeds/FieldMappingPage';
import CustomersPage from './features/customers/CustomersPage';
import CustomerContactsPage from './features/customer-contacts/CustomerContactsPage';
import ProjectRequestsListPage from './features/project-intake/ProjectRequestsListPage';
import DealsPage from './features/deals/DealsPage';
import ProjectsListPage from './features/projects/ProjectsListPage';
import ProjectBoardPage from './features/tasks/ProjectBoardPage';
import CasesPage from './features/cases/CasesPage';
import LoginPage from './features/auth/LoginPage';
import UserManagementPage from './features/users/UserManagementPage';
import RolesManagementPage from './features/roles/RolesManagementPage';
import TeamsPage from './features/teams/TeamsPage';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { useFcm } from './hooks/useFcm';
import Dashboard from './features/dashboard/DashboardPage';
import LoadingSpinner from './components/LoadingSpinner';

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading, token } = useAuth();
  const [timeoutReached, setTimeoutReached] = useState(false);
  
  useEffect(() => {
    if (loading) {
      const timer = setTimeout(() => {
        console.warn('Auth loading timeout reached');
        setTimeoutReached(true);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [loading]);
  
  // If timeout reached or no token/auth, redirect to login
  if (timeoutReached || (!loading && !token && !isAuthenticated)) {
    return <Navigate to="/login" replace />;
  }
  
  // Show loading spinner
  if (loading && !timeoutReached) {
    return <LoadingSpinner fullScreen message="Authenticating..." />;
  }
  
  return <>{children}</>;
}

function AppLayout() {
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { user } = useAuth();

  useFcm(user?.id);

  return (
    <div className="h-screen overflow-hidden theme-bg-primary">
      <div className="relative z-10 flex h-screen overflow-hidden">
        <AnimatePresence initial={false} mode="popLayout">
          {sidebarOpen && (
            <motion.div
              key="sidebar"
              initial={{ x: -280, opacity: 0.6 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -320, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 260, damping: 26 }}
              style={{ willChange: 'transform, opacity' }}
            >
              <Sidebar />
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex-1 flex flex-col min-w-0">
          <Header
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            viewMode={viewMode}
            setViewMode={setViewMode}
          />
          <main className="flex-1 overflow-y-auto p-4 theme-bg-secondary">
            <div className="w-full h-full">
              <Routes>
                <Route
                  path="/"
                  element={<Dashboard />}
                />
                <Route path="/tasks" element={<TasksPage />} />
                <Route path="/tasks/:id" element={<TaskDetailsPage />} />
                <Route path="/intake" element={<ProjectIntakePage />} />
                <Route path="/form" element={<FormsListPage />} />
                <Route path="/form/builder" element={<FormBuilderPage />} />
                <Route path="/form/builder/:id" element={<FormBuilderPage />} />
                <Route path="/form/:id/submissions" element={<FormSubmissionsPage />} />
                <Route path="/form/:id" element={<DynamicFormPage />} />
                <Route path="/form-feeds" element={<FormFeedsPage />} />
                <Route path="/form-feeds/create" element={<CreateFeedPage />} />
                <Route path="/form-feeds/edit/:id" element={<EditFeedPage />} />
                <Route path="/form-feeds/mapping/:formId" element={<FieldMappingPage />} />
                <Route path="/customers" element={<CustomersPage />} />
                <Route path="/customer-contacts" element={<CustomerContactsPage />} />
                <Route path="/project-requests" element={<ProjectRequestsListPage />} />
                <Route path="/deals" element={<DealsPage />} />
                <Route path="/projects" element={<ProjectsListPage />} />
                <Route path="/projects/:id/board" element={<ProjectBoardPage />} />
                <Route path="/projects/board" element={<ProjectBoardPage />} />
                <Route path="/cases" element={<CasesPage />} />
                <Route path="/users" element={<UserManagementPage />} />
                <Route path="/roles" element={<RolesManagementPage />} />
                <Route path="/teams" element={<TeamsPage />} />
              </Routes>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <Toaster
              position="top-right"
              reverseOrder={false}
              gutter={8}
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#fff',
                  color: '#363636',
                  padding: '16px',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                },
                success: {
                  duration: 3000,
                  iconTheme: {
                    primary: '#10b981',
                    secondary: '#fff',
                  },
                },
                error: {
                  duration: 5000,
                  iconTheme: {
                    primary: '#ef4444',
                    secondary: '#fff',
                  },
                },
              }}
            />
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              {/* Public form route - accessible without authentication */}
              <Route path="/verify-form" element={<VerifySharedFormPage />} />
              <Route path="/f/:handler" element={<PublicFormPage />} />
              {/* All /form/* routes (builder, dynamic, submissions) are handled inside AppLayout */}
              <Route
                path="/*"
                element={
                  <RequireAuth>
                    <AppLayout />
                  </RequireAuth>
                }
              />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </Provider>
  );
}

export default App;
